// MenuList1.jsx
import React, { useContext, useEffect, useLayoutEffect, useState } from "react";
import { ChevronDownIcon, ChevronRightIcon } from "@heroicons/react/solid";
import icons from "../icons";
import actions from "../actions";
import { useLocation } from "react-router-dom";
import classNames from "classnames";
import ConfigContext from "../contexts/AppContext";
import { useHistory } from "react-router-dom";
import uuid from "react-uuid";
import styled, { ThemeContext } from "styled-components";
import AppContext from "../contexts/AppContext";
import LeftBarContext from "../contexts/LeftBarContext";
import WithLoader from "./WithLoader";
import { createBrowserHistory } from "history";
import appFetch from "../appFetch";
import apiEndpoints from "../apiEndpoints";
import { appendQueryToUri } from "../util/generalUtil";

const Badge = styled.div`
  width: 18px;
  height: 18px;
  background: ${({ theme, active }) =>
    active ? theme?.colors.primary : theme?.colors.accent};
  font-size: 0.7em;
  color: ${({ theme, active }) => (active ? "#ffffff" : theme?.colors.primary)};
`;

const MenuText = styled.div`
  color: ${({ theme, active }) => (active ? theme.colors?.primary : "#838383")};
  font-weight: ${({ active }) => (active ? "600" : "normal")};
`;

// menu3LevelDummy.js (or in same file for a quick test)
const menu3LevelDummy = [
  {
    name: "Menu 1",
    icon: "Users",
    options: [
      {
        name: "Sub menu 1",
        icon: "setup",
        options: [
          {
            name: "Sub sub menu 1",
            icon: "Forms",
            target: "menu",
            menu_type: "dashboard",
            targetid: 3001,
          },
          {
            name: "Sub sub menu 2",
            icon: "Forms",
            target: "menu",
            menu_type: "dashboard",
            targetid: 3002,
          },
        ],
      },
      {
        name: "Sub menu 2",
        icon: "setup",
        target: "menu",
        menu_type: "dashboard",
        targetid: 3003,
      },
    ],
  },
  {
    name: "Menu 2",
    icon: "Users",
    options: [
      {
        name: "Sub menu A",
        icon: "Forms",
        options: [
          {
            name: "Sub sub menu A1",
            icon: "Forms",
            target: "menu",
            menu_type: "dashboard",
            targetid: 3004,
          },
          {
            name: "Sub sub menu A2",
            icon: "Forms",
            options: [
              {
                name: "Level 4 sample node",
                icon: "Forms",
                target: "menu",
                menu_type: "dashboard",
                targetid: 3005,
              },
            ],
          },
        ],
      },
    ],
  },
  // also include a plain, non-grouped item to ensure previous behavior still works:
  {
    name: "Logs",
    icon: "Error",
    target: "menu",
    menu_type: "dashboard",
    targetid: 2231,
  },
];

function MenuList1({ options: propsOptions, name, setLoading, onClick }) {
  const { config, setConfig } = useContext(ConfigContext);
  const theme = useContext(ThemeContext);
  const { context, setContext } = useContext(AppContext);
  const { menuCollapse, setMobileMenuOpen } = useContext(LeftBarContext);
  // const history = useHistory();

  // ---------- NEW: converter utilities (detect levels + nest)
  const detectGroupingLevels = (arr = []) => {
    const candidates = ["group", "subgroup", "subsubgroup", "subsubsubgroup"];
    const levels = [];
    for (const c of candidates) {
      if (
        arr.some((it) => it && typeof it[c] !== "undefined" && it[c] !== null)
      ) {
        levels.push(c);
      }
    }
    return levels;
  };

  /**
   * Nest flat options into a tree using the given level keys.
   * Keeps icon fields for group levels by using <level>icon if present.
   */
  const nestByLevels = (flat = [], levels = ["group", "subgroup"]) => {
    const rootList = [];
    const rootMap = new Map();

    for (const item of flat) {
      let currentList = rootList;
      let currentMap = rootMap;

      for (const lvl of levels) {
        const val = item && (item[lvl] ?? null);
        const iconField = `${lvl}icon`;

        if (val) {
          const key = String(val).trim();
          let node = currentMap.get(key);
          if (!node) {
            node = {
              name: key,
              icon: item[iconField] ?? undefined,
              options: [],
              __map: new Map(),
            };
            currentMap.set(key, node);
            currentList.push(node);
          }
          currentList = node.options;
          currentMap = node.__map;
        } else {
          break;
        }
      }

      // leaf: clone original item and remove grouping props so MenuList sees it as a leaf
      const leaf = { ...item };
      for (const lvl of levels) {
        delete leaf[lvl];
        delete leaf[`${lvl}icon`];
      }
      currentList.push(leaf);
    }

    // cleanup internal maps
    const cleanup = (arr) => {
      for (const node of arr) {
        if (node && node.__map) {
          if (Array.isArray(node.options)) cleanup(node.options);
          delete node.__map;
        }
      }
    };
    cleanup(rootList);

    return rootList;
  };

  // -------- end converter utilities

  // if the incoming propsOptions uses group/subgroup fields, convert them to nested structure
  let options = propsOptions;
  const detectedLevels = Array.isArray(propsOptions)
    ? detectGroupingLevels(propsOptions)
    : [];
  let convertedFromFlat = null;
  if (detectedLevels.length > 0) {
    // convert using the detected levels, e.g. ['group','subgroup']
    convertedFromFlat = nestByLevels(propsOptions, detectedLevels);
    options = convertedFromFlat;
  }

  const history = createBrowserHistory();
  const {
    location: { pathname },
  } = history;

  // normalize current path and build patt
  let _pathname = pathname.split("/").slice(-1)[0]?.toLowerCase() || "home";
  _pathname = decodeURI(_pathname.replace(/\//, "")).replace(/\-/g, " ");
  let patt = RegExp(_pathname, "i");
  console.log("patt", patt);

  // helper: recursive find by name (legacy) — keeps behavior but supports nested items
  const findActiveMenuByName = (items = [], patt) => {
    for (const it of items) {
      if (patt.test(it?.name)) return it;
      if (Array.isArray(it?.options)) {
        const found = findActiveMenuByName(it.options, patt);
        if (found) return found;
      }
    }
    return null;
  };

  // NEW: recursive find by targetid (unique)
  const findByTargetId = (items = [], id) => {
    if (!Array.isArray(items) || id == null) return null;
    for (const it of items) {
      if (
        it &&
        typeof it.targetid !== "undefined" &&
        String(it.targetid) === String(id)
      ) {
        return it;
      }
      if (Array.isArray(it?.options)) {
        const found = findByTargetId(it.options, id);
        if (found) return found;
      }
    }
    return null;
  };

  // Determine activeMenu: prefer context.menuId (unique) -> fallback to pathname name matching
  let activeMenu = null;
  try {
    if (context && context.menuId) {
      activeMenu = findByTargetId(options, context.menuId);
    }
  } catch (err) {
    // ignore and fallback
    console.warn("findByTargetId error", err);
  }
  if (!activeMenu) {
    activeMenu = findActiveMenuByName(options, patt);
  }

  let { targetid: menuId } = activeMenu || {};

  console.log("the active menu is", activeMenu);

  useEffect(() => {
    // On initial load, sync menuId from URL-derived activeMenu
    if (!context?.menuId && activeMenu?.targetid) {
      setContext((prev) => ({
        ...prev,
        menuId: activeMenu.targetid,
      }));
    }
  }, [activeMenu?.targetid]);

  const currentPath = pathname.split("/").slice(-1)[0]?.toLowerCase() || "home";

  useEffect(() => {
    // keep context.menuId in sync with changes to pathname-derived menuId
    // but do not overwrite if context already has a value (we set it on click)
    setContext((prev) => ({ ...prev, menuId }));
  }, [pathname]);

  console.log("contextData", context);

  console.log("initial options", options);

  // Grouping logic preserved exactly
  let groupedOrder = {};
  let optionsGroup = [];
  let optionsGroupLen = -1;

  // If we converted from flat -> nested already, the top-level is already grouped
  // So skip re-grouping and assign directly. Otherwise use original grouping loop.
  if (convertedFromFlat) {
    optionsGroup = options;
  } else {
    options.forEach((o) => {
      if (o.group) {
        const groupKey = o.group.trim(); // normalization, optionally use toLowerCase()

        if (!(groupKey in groupedOrder)) {
          groupedOrder[groupKey] = ++optionsGroupLen;
        }

        const index = groupedOrder[groupKey];

        if (!optionsGroup[index]) {
          optionsGroup[index] = {
            name: groupKey,
            icon: o.groupicon,
            options: [o],
          };
        } else {
          optionsGroup[index].options.push(o);
        }
      } else {
        optionsGroup[++optionsGroupLen] = o;
      }
    });
  }

  console.log("optionsGroup", optionsGroup);

  const loadPage = (item) => {
    setLoading(true);

    const postData = {
      urlMenu: null,
      layoutId: null,
      targetId: item.targetid,
      target: "MENU",
      schemaId: null,
      txnGroup: null,
      parentDropValue: null,
    };

    appFetch
      .get(appendQueryToUri(apiEndpoints.menuView, postData))
      .then((res) => {
        // push new url first (this triggers locationchange because MainArea patches pushState)
        const link = `${item.name.toLowerCase()}`.replace(/\s/g, "-");
        history.push(link);

        // Safely update context using functional setter so we never mutate stale objects
        setContext((prev) => {
          const page = { ...(prev.pageConfig ?? {}) };

          // clone the top-level subcomponents array shallowly (safe)
          const existing = Array.isArray(page.subcomponents)
            ? page.subcomponents
            : [];
          const leftArea = existing[0] ?? null;
          const rightArea = existing[2] ?? null;

          // Insert the server-provided middle pane as the main area
          page.subcomponents = [leftArea, res, rightArea];

          return {
            ...prev,
            menuId: item.targetid,
            pageConfig: page,
          };
        });
      })
      .catch((e) => console.log(e.message))
      .finally(() => setLoading(false));
  };

  const openNewPage = (item) => {
    // --- CRITICAL FIX: set context.menuId immediately to select correct active item
    setContext((prev) => ({ ...prev, menuId: item.targetid }));
    setMobileMenuOpen(true);
    console.log(
      "the open new page function",
      item.name.toLowerCase(),
      activeMenu?.name?.toLowerCase(),
    );
    // avoid no-op comparing names; instead compare id if available
    if (String(item.targetid) === String(activeMenu?.targetid)) return;
    loadPage(item);
  };

  // const setContextMenuId = (menuId) => {
  //     setContext(prev => ({...prev, menuId}))
  // }

  // ICON MAP preserved exactly (no changes)
  const iconMap = {
    home: "HomeIcon",
    setup: "SetupIcon",
    transaction: "TransactionIcon",
    approval: "ApprovalIcon",
    memo: "MemoIcon",
    open: "OpenIcon",
    dashboard: "DashboardIcon",
    confirmation: "ConfirmationIcon",
    payrollReports: "PayrollReportsIcon",
    otherReports: "OtherReportsIcon",
    essReports: "EssReportsIcon",
    bioData: "BioDataIcon",
    Online: "OnlineIcon",
    Users: "UsersIcon",
    Retail: "RetailIcon",
    Wallet: "WalletIcon",
    Ussd: "UssdIcon",
    Draws: "DrawsIcon",
    ThirdParty: "ThirdPartyIcon",
    Accuracy: "AccuracyIcon",
    Feeds: "FeedsIcon",
    Whatsapp: "WhatsappIcon",
    Error: "ErrorIcon",
    Calender: "CalenderIcon",
    map: "MapIcon",
    Statistics: "StatisticsIcon",
    OtherReports: "OtherReportsIcon",
    Ticket: "TicketIcon",
    Search: "SearchIcon",
    report: "OtherReportsIcon",
    Forms: "FormsIcon",
    module: "FormsIcon",
    reports: "StatisticsIcon",
    money: "MoneyIcon",
    action: "ActionIcon",
    followup: "FollowUpIcon",
    pending: "PendingIcon",
    qualified: "PassIcon",
    schedule: "ScheduledIcon",
    demo: "DemoIcon",
    proposal: "PlanIcon",
    onboarding: "OnboardIcon",
    crm: "CRMIcon",
    sales: "SalesIcon",
    inventory: "InventoryIcon",
    horizontalbarchart: "HorizontalBarIcon",
    verticalbarchart: "VerticalBarIcon",
    scatterchart: "ScatterIcon",
    layout: "LayoutIcon",
    piechart: "PieIcon",
    linechart: "LineIcon",
    bell: "BellIcon",
    User: "UserIcon",
    Browser: "InternetIcon",
    All: "AllIcon",
    Pos: "PosIcon",
    Football: "FootballIcon",
    Bingo: "BingoIcon",
    Lottery: "LotteryIcon",
    channels: "ChannelsIcon",
    "": "setup",
  };

  // ---------- NEW: normalize optionsGroup to attach stable _key to every item (preserves original arrays)
  const normalize = (items = [], parentKey = "") =>
    items.map((i, idx) => {
      const keyParts = [parentKey];
      if (i.group) keyParts.push(i.group);
      if (i.subgroup) keyParts.push(i.subgroup);
      keyParts.push(i.name);
      const key = keyParts.join("-") + `-${idx}`;

      return {
        ...i,
        options: Array.isArray(i.options)
          ? normalize(i.options, `${key}-`)
          : i.options,
        _key: key,
      };
    });

  const normalizedOptionsGroup = normalize(optionsGroup);

  const hasGrandchildren = (item) =>
    Array.isArray(item?.options) &&
    item.options.some(
      (child) => Array.isArray(child?.options) && child.options.length > 0,
    );

  const isItemOrDescendantActive = (item, activeMenuId) => {
    if (!item) return false;

    if (
      typeof item.targetid !== "undefined" &&
      activeMenuId != null &&
      String(item.targetid) === String(activeMenuId)
    ) {
      return true;
    }

    if (item.options) {
      return item.options.some((child) =>
        isItemOrDescendantActive(child, activeMenuId),
      );
    }

    return false;
  };

  // ---------- Keep Tree and TreeSub names but adapt them to use normalization + recursive rendering
  const Tree = ({ item, current, isExpanded, toggleGroup }) => {
    const [activeItem, setActiveItem] = useState();
    const isThreeLevel = hasGrandchildren(item);
    const showSubOptions = (item) => {
      if (item.options) return toggleGroup();
      return openNewPage(item);
    };

    const { name, icon, badge, options } = item;
    const menuIcon = iconMap[icon] || "setup";

    // Prefer the context.menuId (we set it on click) and fall back to activeMenu from pathname
    const active = isItemOrDescendantActive(
      item,
      isThreeLevel
        ? ((context && context.menuId) ?? activeMenu?.targetid)
        : activeMenu?.targetid,
    );

    console.log("what is this", item, active, activeMenu);

    return (
      <div>
        <li
          className="mb-3 flex items-center"
          onClick={() => showSubOptions(item)}
        >
          {icons[menuIcon] &&
            React.createElement(icons[menuIcon], {
              width: 20,
              height: null,
              fill: active ? theme?.colors?.primary : "#AAAAAA",
              stroke: active ? theme?.colors?.primary : "#AAAAAA",
            })}

          <MenuText
            className={classNames(
              "capitalize leading-1 text-gray-custom tracking-wide flex items-center",
              { "pl-3": !!icons[menuIcon] },
            )}
            active={active}
          >
            <span className="pr-2">{item.name}</span>

            {typeof badge !== "undefined" && (
              <Badge className="font-semibold ml-2 rounded-sm inline-grid place-items-center rounded-xl">
                {badge}
              </Badge>
            )}

            {item.options && (
              <span className="rounded-full shadow">
                {isExpanded ? (
                  <ChevronDownIcon width={18} height={18} />
                ) : (
                  <ChevronRightIcon width={18} height={18} />
                )}
              </span>
            )}
          </MenuText>
        </li>

        {item.options && isExpanded && (
          <TreeSub item={item.options} setActiveItem={setActiveItem} />
        )}
      </div>
    );
  };

  // Recursive renderer for nested options. This replaces the previous flat 2-level-only TreeSub.
  // It preserves the visual spacing you used (ml-6) and the same icon/menu rendering.
  const TreeSub = ({ item, setActiveItem, level = 1 }) => {
    // item is an array of options at this level
    return (
      <ul>
        {item.map((option, idx) => {
          let { name, icon, badge, options } = option;
          let menuIcon = iconMap[icon];

          // Use targetid-based recursive active check so parents highlight correctly
          const active = isItemOrDescendantActive(
            option,
            (context && context.menuId) ?? activeMenu?.targetid,
          );

          // keep compatibility with previous setActiveItem behavior (sets it)
          setActiveItem && setActiveItem(activeMenu);

          const onClickHandler = (ev) => {
            // stop event bubbling only if we have nested options and we want toggle
            ev.stopPropagation();
            if (options) {
              // toggle expansion using the normalized _key
              setExpandedGroups((prev) => ({
                ...prev,
                [option._key]: !prev[option._key],
              }));
            } else {
              openNewPage(option);
            }
          };

          return (
            <React.Fragment key={option._key || `${name}-${idx}`}>
              <li
                className="mb-2 flex items-center cursor-pointer"
                style={{ marginLeft: `${level * 1.5}rem` }}
                onClick={onClickHandler}
              >
                {icons[menuIcon] &&
                  React.createElement(icons[menuIcon], {
                    width: 20,
                    height: null,
                    fill: active ? theme?.colors?.primary : "#AAAAAA",
                    stroke: active ? theme?.colors?.primary : "#AAAAAA",
                  })}
                <MenuText
                  className="flex capitalize pl-3 leading-1 text-gray-custom tracking-wide flex items-center"
                  active={active}
                >
                  <span>{name}</span>
                  {typeof badge != "undefined" && (
                    <Badge className="font-semibold ml-2 rounded-sm inline-grid place-items-center rounded-xl">
                      {badge}
                    </Badge>
                  )}
                </MenuText>

                {options && (
                  <span className="ml-2 rounded-full shadow">
                    {expandedGroups[option._key] ? (
                      <ChevronDownIcon
                        width={16}
                        fill={active ? theme?.colors?.primary : "#AAAAAA"}
                      />
                    ) : (
                      <ChevronRightIcon width={16} />
                    )}
                  </span>
                )}
              </li>

              {/* If this option has children and is expanded, recurse */}
              {options && expandedGroups[option._key] && (
                <TreeSub
                  item={options}
                  setActiveItem={setActiveItem}
                  level={level + 1}
                />
              )}
            </React.Fragment>
          );
        })}
      </ul>
    );
  };

  const [showGroup, setShowGroup] = useState(true);
  const [expandedGroups, setExpandedGroups] = useState({});

  // Toggle function — kept but now accepts either numeric idx or string key
  const toggleGroup = (indexOrKey) => {
    setExpandedGroups((prev) => ({
      ...prev,
      [indexOrKey]: !prev[indexOrKey],
    }));
  };

  const showSubOptions = (item) => {
    if (item.options) return setShowGroup(!showGroup);
    return openNewPage(item);
  };

  // console.log("the menucollapse ish is ", menuCollapse);
  return (
    <div className="py-2 z-50">
      {!menuCollapse && (
        <>
          {/* <header className="font-semibold mb-3 tracking-wide">{name}</header> */}
          <header className="flex items-center mb-6 border-y border-gray-200 bg-gray-100 -mx-8 ">
            <h3 className="font-semibold tracking-wide ml-8 py-1">{name}</h3>
          </header>
          <ul>
            {normalizedOptionsGroup?.map((item, idx) => {
              const activeId =
                (context && context.menuId) ?? activeMenu?.targetid;

              const shouldBeExpanded =
                expandedGroups[item._key] ||
                isItemOrDescendantActive(item, activeId);

              return (
                <li
                  key={item._key || idx}
                  className="mb-3 items-center cursor-pointer"
                >
                  <Tree
                    item={item}
                    current={idx}
                    // pass the new normalized key for expansion state
                    isExpanded={shouldBeExpanded}
                    toggleGroup={() => toggleGroup(item._key)}
                  />
                </li>
              );
            })}
          </ul>
        </>
      )}

      {menuCollapse && (
        <>
          {/* <header className="font-semibold mb-3 uppercase tracking-wide">{name}</header> */}
          <ul>
            {normalizedOptionsGroup?.map((item, idx) => {
              let { name, icon, badge } = item;
              let menuIcon = iconMap[icon];
              // Use same id-based active calculation for collapsed view
              const active = isItemOrDescendantActive(
                item,
                (context && context.menuId) ?? activeMenu?.targetid,
              );
              return (
                <li
                  key={item._key || idx}
                  className="group mb-6 flex items-center cursor-pointer"
                  onClick={() => showSubOptions(item)}
                >
                  <div className="relative">
                    {icons[menuIcon] &&
                      React.createElement(icons[menuIcon], {
                        width: 20,
                        height: null,
                        fill: active ? theme?.colors?.primary : "#AAAAAA",
                        stroke: active ? theme?.colors?.primary : "#AAAAAA",
                      })}
                    {typeof badge != "undefined" && (
                      <Badge
                        active={active}
                        className="absolute top-0 -translate-y-3 font-semibold ml-2 rounded-sm inline-grid place-items-center rounded-xl"
                      >
                        {badge}
                      </Badge>
                    )}
                  </div>
                  {/* <div className="text-md transform origin-bottom-left transition-all duration-100 none group-hover:block group-hover:text-primary capitalize pl-3 leading-1 tracking-wide flex items-center" active={active}>
                                <span>{name}</span>
                                {typeof badge != "undefined" && <Badge className="font-semibold ml-2 rounded-sm inline-grid place-items-center rounded-xl">{badge}</Badge>}
                            </div> */}
                </li>
              );
            })}
          </ul>
        </>
      )}
    </div>
  );
}

export default WithLoader(MenuList1);